import { useCallback, useMemo } from "react";

import { queryKeys } from "@/services/react-query.service/query-keys";
import { useQueryClient } from "@tanstack/react-query";

import { ENABLE_FEATURE_FLAG_OVERRIDE_KEY, FeatureFlagIDs } from "../../models/config";
import { FeatureFlag } from "../../models/feature-flag-model";
import { useFeatureFlagStore } from "../use-feature-flag-store";

interface UseFeatureFlagCachedProps {
  workspaceId: string;
  id: FeatureFlagIDs;
  disableOverride?: boolean;
}

export const useFeatureFlagCached = ({ workspaceId, id, disableOverride = false }: UseFeatureFlagCachedProps) => {
  const queryClient = useQueryClient();
  const { featureFlagList: featureFlagListStore } = useFeatureFlagStore();

  // Get the cached feature flag list from the preloader
  const cachedFeatureFlagList = queryClient.getQueryData<FeatureFlag[]>(queryKeys.featureFlags.list(workspaceId));

  const getFeatureFlag = useCallback(
    (id: string) => featureFlagListStore.find((f) => f.id === id),
    [featureFlagListStore],
  );

  const canOverride = useMemo(() => {
    if (!cachedFeatureFlagList) return false;
    return cachedFeatureFlagList.find((flag) => flag.id === ENABLE_FEATURE_FLAG_OVERRIDE_KEY)?.enabled;
  }, [cachedFeatureFlagList]);

  const featureFlag = useMemo(() => {
    if (!cachedFeatureFlagList) {
      return null;
    }

    const data = cachedFeatureFlagList.find((flag) => flag.id === id);
    if (!data) {
      return null;
    }

    const featureFlagOverride = getFeatureFlag(id);
    const shouldOverride = !disableOverride && !!canOverride && !!featureFlagOverride;

    try {
      const payload: unknown = data.payload ? JSON.parse(data.payload) : null;

      return {
        ...data,
        enabled: shouldOverride ? featureFlagOverride.enabled : data.enabled,
        payload,
      };
    } catch (_error) {
      return {
        ...data,
        enabled: shouldOverride ? featureFlagOverride.enabled : data.enabled,
        payload: null,
      };
    }
  }, [cachedFeatureFlagList, id, getFeatureFlag, canOverride, disableOverride]);

  const isEnabled = useMemo(() => {
    return featureFlag?.enabled || false;
  }, [featureFlag]);

  const loading = !cachedFeatureFlagList;

  return {
    featureFlag,
    isEnabled,
    loading,
    error: null, // No error since we're using cached data
  };
};
